import { Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { CustomerLayout } from './components/CustomerLayout';
import { firstPage, StaffLayout } from './components/StaffLayout';
import { Spinner } from './components/ui';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CustomerAuth, FullPageSpinner, RequirePerm, StaffAuth, useStaff } from './lib/auth';
import { safeLazy } from './lib/safeLazy';
import StaffLogin from './pages/staff/Login';
import CustomerLogin from './pages/customer/Login';
import { initTheme } from './pages/staff/engagement/theme';

// Light or dark, as chosen on this device, before the first paint.
initTheme();

const s = {
  Dashboard: safeLazy(() => import('./pages/staff/Dashboard')),
  Gate: safeLazy(() => import('./pages/staff/Gate')),
  SecurityHome: safeLazy(() => import('./pages/staff/security/SecurityHome')),
  VisitDetail: safeLazy(() => import('./pages/staff/VisitDetail')),
  Visits: safeLazy(() => import('./pages/staff/Visits')),
  VehicleDetail: safeLazy(() => import('./pages/staff/VehicleDetail')),
  Customers: safeLazy(() => import('./pages/staff/Customers')),
  CustomerDetail: safeLazy(() => import('./pages/staff/CustomerDetail')),
  Imports: safeLazy(() => import('./pages/staff/Imports')),
  ImportDetail: safeLazy(() => import('./pages/staff/ImportDetail')),
  HistoryImportDetail: safeLazy(() => import('./pages/staff/imports/HistoryImportDetail')),
  Collect: safeLazy(() => import('./pages/staff/Collect')),
  Payments: safeLazy(() => import('./pages/staff/Payments')),
  PaymentDetail: safeLazy(() => import('./pages/staff/PaymentDetail')),
  Closing: safeLazy(() => import('./pages/staff/Closing')),
  Coupons: safeLazy(() => import('./pages/staff/Coupons')),
  Finance: safeLazy(() => import('./pages/staff/Finance')),
  AccountGroups: safeLazy(() => import('./pages/staff/AccountGroups')),
  Reports: safeLazy(() => import('./pages/staff/Reports')),
  DailyReport: safeLazy(() => import('./pages/staff/reports/DailyReport')),
  OwnerDailyReport: safeLazy(() => import('./pages/staff/reports/OwnerDaily')),
  VisitorsReport: safeLazy(() => import('./pages/staff/reports/Visitors')),
  Pricing: safeLazy(() => import('./pages/staff/Pricing')),
  Campaigns: safeLazy(() => import('./pages/staff/Campaigns')),
  Notifications: safeLazy(() => import('./pages/staff/Notifications')),
  Outlets: safeLazy(() => import('./pages/staff/Outlets')),
  Users: safeLazy(() => import('./pages/staff/Users')),
  Settings: safeLazy(() => import('./pages/staff/Settings')),
  Audit: safeLazy(() => import('./pages/staff/Audit')),
  Account: safeLazy(() => import('./pages/staff/Account')),
  Guide: safeLazy(() => import('./pages/staff/Guide')),
  Pos: safeLazy(() => import('./pages/staff/shop/Pos')),
  Bills: safeLazy(() => import('./pages/staff/shop/Bills')),
  BillDetail: safeLazy(() => import('./pages/staff/shop/BillDetail')),
  EditBill: safeLazy(() => import('./pages/staff/shop/EditBill')),
  BillPrint: safeLazy(() => import('./pages/staff/shop/BillPrint')),
  Products: safeLazy(() => import('./pages/staff/shop/Products')),
  Inventory: safeLazy(() => import('./pages/staff/shop/Inventory')),
  ShopPnl: safeLazy(() => import('./pages/staff/shop/ShopPnl')),
  ShopsOverview: safeLazy(() => import('./pages/staff/shop/ShopsOverview')),
  DailySales: safeLazy(() => import('./pages/staff/shop/DailySales')),
  ShopOpenDays: safeLazy(() => import('./pages/staff/shop/ShopOpenDays')),
  ShopsToday: safeLazy(() => import('./pages/staff/shop/ShopsToday')),
  ShopStaff: safeLazy(() => import('./pages/staff/shop/ShopStaff')),
  ShopSettings: safeLazy(() => import('./pages/staff/shop/ShopSettings')),
  Cameras: safeLazy(() => import('./pages/staff/anpr/Cameras')),
  Areas: safeLazy(() => import('./pages/staff/anpr/Areas')),
  EntryPass: safeLazy(() => import('./pages/staff/parking/EntryPass')),
  Visitors: safeLazy(() => import('./pages/staff/parking/Visitors')),
  LiveStatus: safeLazy(() => import('./pages/staff/parking/LiveStatus')),
  CctvDashboard: safeLazy(() => import('./pages/staff/cctv/CctvDashboard')),
  Games: safeLazy(() => import('./pages/staff/activities/Games')),
  Turf: safeLazy(() => import('./pages/staff/activities/Turf')),
  Events: safeLazy(() => import('./pages/staff/activities/Events')),
  TrackDesk: safeLazy(() => import('./pages/staff/activities/karts/TrackDesk')),
  TrackDisplay: safeLazy(() => import('./pages/staff/activities/karts/TrackDisplay')),
  KartFleet: safeLazy(() => import('./pages/staff/activities/karts/Fleet')),
  KartReport: safeLazy(() => import('./pages/staff/activities/karts/KartReport')),
  Appointments: safeLazy(() => import('./pages/staff/activities/Appointments')),
  Settlements: safeLazy(() => import('./pages/staff/settlements/Settlements')),
  Agreements: safeLazy(() => import('./pages/staff/settlements/Agreements')),
  Closures: safeLazy(() => import('./pages/staff/settlements/Closures')),
  Advertisements: safeLazy(() => import('./pages/staff/settlements/Advertisements')),
  CleaningCharges: safeLazy(() => import('./pages/staff/settlements/CleaningCharges')),
  Generator: safeLazy(() => import('./pages/staff/settlements/Generator')),
  Dues: safeLazy(() => import('./pages/staff/settlements/Dues')),
  ShopLedger: safeLazy(() => import('./pages/staff/settlements/ShopLedger')),
  RentLedger: safeLazy(() => import('./pages/staff/shop/RentLedger')),
  ShopMaintenance: safeLazy(() => import('./pages/staff/shop/ShopMaintenance')),
  Housekeeping: safeLazy(() => import('./pages/staff/facility/Housekeeping')),
  Quality: safeLazy(() => import('./pages/staff/facility/Quality')),
  PestControl: safeLazy(() => import('./pages/staff/facility/PestControl')),
  Maintenance: safeLazy(() => import('./pages/staff/facility/Maintenance')),
  Assets: safeLazy(() => import('./pages/staff/facility/Assets')),
  OtherServices: safeLazy(() => import('./pages/staff/facility/OtherServices')),
  Employees: safeLazy(() => import('./pages/staff/people/Employees')),
  Attendance: safeLazy(() => import('./pages/staff/people/Attendance')),
  Payroll: safeLazy(() => import('./pages/staff/people/Payroll')),
  HrReminders: safeLazy(() => import('./pages/staff/people/HrReminders')),
  Accommodation: safeLazy(() => import('./pages/staff/people/Accommodation')),
  Materials: safeLazy(() => import('./pages/staff/supply/Materials')),
  MaterialRequests: safeLazy(() => import('./pages/staff/supply/MaterialRequests')),
  Procurement: safeLazy(() => import('./pages/staff/supply/Procurement')),
  SupplierBills: safeLazy(() => import('./pages/staff/supply/SupplierBills')),
  Cards: safeLazy(() => import('./pages/staff/engagement/Cards')),
  Reception: safeLazy(() => import('./pages/staff/reception/Recharge')),
  ReceptionHome: safeLazy(() => import('./pages/staff/reception/home/ReceptionHome')),
  RidesDesk: safeLazy(() => import('./pages/staff/reception/rides/RidesDesk')),
  WalletReport: safeLazy(() => import('./pages/staff/reports/WalletReport')),
  PointsHistory: safeLazy(() => import('./pages/staff/reports/PointsHistory')),
  Feedback: safeLazy(() => import('./pages/staff/engagement/Feedback')),
  Terms: safeLazy(() => import('./pages/staff/engagement/Terms')),
  PrinterSettings: safeLazy(() => import('./pages/staff/PrinterSettings')),
  ShopHome: safeLazy(() => import('./pages/staff/shop/ShopHome')),
  LiveActivity: safeLazy(() => import('./pages/staff/live/LiveActivity')),
  SettlementPrint: safeLazy(() => import('./pages/staff/settlements/SettlementPrint')),
  AdInvoicePrint: safeLazy(() => import('./pages/staff/settlements/AdInvoicePrint')),
  RechargeReceipt: safeLazy(() => import('./pages/staff/engagement/RechargeReceipt')),
  WalletStatement: safeLazy(() => import('./pages/staff/engagement/WalletStatement')),
  PurchaseOrderPrint: safeLazy(() => import('./pages/staff/supply/PurchaseOrderPrint')),
  SupplierBillPrint: safeLazy(() => import('./pages/staff/supply/SupplierBillPrint')),
  // My business (the owner's view): marketing bought, renovation & works, the venue's own P&L.
  Marketing: safeLazy(() => import('./pages/staff/venue/Marketing')),
  Works: safeLazy(() => import('./pages/staff/venue/Works')),
  EarlierRecords: safeLazy(() => import('./pages/staff/earlier/EarlierRecords')),
  VenuePnl: safeLazy(() => import('./pages/staff/venue/VenuePnl')),
  Presence: safeLazy(() => import('./pages/staff/presence/Presence')),
  DailyPulse: safeLazy(() => import('./pages/staff/reports/DailyPulse')),
  // My business: the owner's capital account, loans and liabilities (the accountant reads it).
  Capital: safeLazy(() => import('./pages/staff/venue/Capital')),
  Electricity: safeLazy(() => import('./pages/staff/settlements/Electricity')),
};

const c = {
  Home: safeLazy(() => import('./pages/customer/Home')),
  Wallet: safeLazy(() => import('./pages/customer/Wallet')),
  Bills: safeLazy(() => import('./pages/customer/Bills')),
  Pay: safeLazy(() => import('./pages/customer/Pay')),
  ScanPay: safeLazy(() => import('./pages/customer/ScanPay')),
  ScanConfirm: safeLazy(() => import('./pages/customer/ScanConfirm')),
  Coupons: safeLazy(() => import('./pages/customer/Coupons')),
  Offers: safeLazy(() => import('./pages/customer/Offers')),
  Turf: safeLazy(() => import('./pages/customer/Turf')),
  History: safeLazy(() => import('./pages/customer/History')),
  Inbox: safeLazy(() => import('./pages/customer/Inbox')),
  Profile: safeLazy(() => import('./pages/customer/Profile')),
  MockCheckout: safeLazy(() => import('./pages/customer/MockCheckout')),
  Feedback: safeLazy(() => import('./pages/customer/Feedback')),
  Terms: safeLazy(() => import('./pages/customer/Terms')),
  WalletStatement: safeLazy(() => import('./pages/staff/engagement/WalletStatement').then((m) => ({ default: m.CustomerWalletStatementPage }))),
};

/**
 * Sends each role to its most useful first screen, which is always one the login can open (a page it may not
 * open would send it straight back here, and the browser would loop).
 */
function StaffHome() {
  const { me, can } = useStaff();
  return <Navigate to={firstPage(me, can)} replace />;
}

/** Facility and supply screens open for the people who run them and for their managers. */
const FACILITY = ['facility.operate', 'facility.manage'];
const SUPPLY = ['supply.operate', 'supply.manage'];
/** Staff users: every login for the super admin, shop logins only for the accountant. */
const USERS = ['users.manage', 'users.shops'];

const P = ({ perm, children }: { perm: string | readonly string[]; children: ReactNode }) => <RequirePerm perm={perm}>{children}</RequirePerm>;

export function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<FullPageSpinner />}>
          <Routes>
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="/admin/login" element={<StaffLogin />} />
          <Route
            path="/admin/visits/:id/pass"
            element={
              <StaffAuth>
                <s.EntryPass />
              </StaffAuth>
            }
          />
          <Route
            path="/admin/bills/:id/print"
            element={
              <StaffAuth>
                <s.BillPrint />
              </StaffAuth>
            }
          />
          {/* Printable documents can still be opened on purpose, without the menu (receipts print on the screen they come from). */}
          <Route path="/admin/settlements/:id/print" element={<StaffAuth><s.SettlementPrint /></StaffAuth>} />
          <Route path="/admin/advertisements/:id/invoice" element={<StaffAuth><s.AdInvoicePrint /></StaffAuth>} />
          <Route path="/admin/cards/receipts/:paymentId" element={<StaffAuth><s.RechargeReceipt /></StaffAuth>} />
          <Route path="/admin/customers/:id/statement" element={<StaffAuth><s.WalletStatement /></StaffAuth>} />
          <Route path="/admin/purchase-orders/:id/print" element={<StaffAuth><s.PurchaseOrderPrint /></StaffAuth>} />
          <Route path="/admin/supplier-bills/:id/print" element={<StaffAuth><s.SupplierBillPrint /></StaffAuth>} />
          {/* The go-kart queue on a TV at the track: full screen, without the menu. */}
          <Route path="/admin/karts/display" element={<StaffAuth><P perm={['karts.operate', 'karts.manage']}><s.TrackDisplay /></P></StaffAuth>} />
          <Route
            path="/admin"
            element={
              <StaffAuth>
                <StaffLayout />
              </StaffAuth>
            }
          >
            <Route index element={<Suspense fallback={<Spinner />}><StaffHome /></Suspense>} />
            <Route path="dashboard" element={<P perm="reports.read"><s.Dashboard /></P>} />
            <Route path="gate" element={<P perm="visits.read"><s.Gate /></P>} />
            {/* Gate security's first screen on a phone: photo + plate of every vehicle, one-tap exits, kept on the phone offline. */}
            <Route path="security" element={<P perm="visits.write"><s.SecurityHome /></P>} />
            <Route path="visits" element={<P perm="visits.read"><s.Visits /></P>} />
            <Route path="visits/:id" element={<P perm="visits.read"><s.VisitDetail /></P>} />
            <Route path="vehicles/:id" element={<P perm="visits.read"><s.VehicleDetail /></P>} />
            <Route path="customers" element={<P perm="customers.lookup"><s.Customers /></P>} />
            <Route path="customers/:id" element={<P perm="customers.lookup"><s.CustomerDetail /></P>} />
            <Route path="imports" element={<P perm="imports.manage"><s.Imports /></P>} />
            <Route path="imports/:id" element={<P perm="imports.manage"><s.ImportDetail /></P>} />
            <Route path="imports/history/:id" element={<P perm="imports.manage"><s.HistoryImportDetail /></P>} />
            <Route path="collect" element={<P perm="payments.record"><s.Collect /></P>} />
            <Route path="payments" element={<P perm="payments.read"><s.Payments /></P>} />
            <Route path="payments/:id" element={<P perm="payments.read"><s.PaymentDetail /></P>} />
            <Route path="closing" element={<P perm="closings.read"><s.Closing /></P>} />
            <Route path="coupons" element={<P perm="coupons.redeem"><s.Coupons /></P>} />
            <Route path="finance" element={<P perm="finance.read"><s.Finance /></P>} />
            <Route path="finance/groups" element={<P perm="finance.read"><s.AccountGroups /></P>} />
            <Route path="reports" element={<P perm="reports.read"><s.Reports /></P>} />
            {/* One day of the whole venue on one page (gate, staff sign-ins, shops, best sellers): the super admin and the accountant. */}
            <Route path="daily-pulse" element={<P perm="shops.overview"><s.DailyPulse /></P>} />
            {/* The day-wise report: gate figures for everyone with reports.read, the shops' figures for venue-wide logins. */}
            <Route path="reports/daily" element={<P perm="reports.read"><s.DailyReport /></P>} />
            {/* The whole venue's day in one report, with PDF and Excel: head office only. */}
            <Route path="reports/day" element={<P perm="shops.overview"><s.OwnerDailyReport /></P>} />
            {/* Who comes to the venue, day by day: head office and the cashier (reports.read), gate staff for their own gates. */}
            <Route path="reports/visitors" element={<P perm={['reports.read', 'visits.read']}><s.VisitorsReport /></P>} />
            {/* Staff presence: head office sees every shop and team, a shop admin their own shop's staff. */}
            <Route path="presence" element={<P perm="presence.read"><s.Presence /></P>} />
            <Route path="pricing" element={<P perm="pricing.read"><s.Pricing /></P>} />
            <Route path="campaigns" element={<P perm="campaigns.read"><s.Campaigns /></P>} />
            <Route path="notifications" element={<P perm="notifications.manage"><s.Notifications /></P>} />
            <Route path="outlets" element={<P perm="outlets.manage"><s.Outlets /></P>} />
            <Route path="users" element={<P perm={USERS}><s.Users /></P>} />
            <Route path="settings" element={<P perm="settings.manage"><s.Settings /></P>} />
            <Route path="audit" element={<P perm="audit.read"><s.Audit /></P>} />
            <Route path="account" element={<s.Account />} />
            {/* "What I can do": every login, its own menu explained. */}
            <Route path="guide" element={<s.Guide />} />
            <Route path="shops" element={<P perm="shops.overview"><s.ShopsOverview /></P>} />
            {/* Every shop for venue-wide logins; a shop login with shop.reports opens it as "Daily sales" for its own shop. */}
            <Route path="shops/daily" element={<P perm={['shops.overview', 'shop.reports']}><s.DailySales /></P>} />
            {/* Which days each shop was open or closed in a month; a shop login opens it as "Open days" for its own shop. */}
            <Route path="shops/open-days" element={<P perm={['shops.overview', 'shop.reports']}><s.ShopOpenDays /></P>} />
            {/* Shops opened / closed, marked by hand: the super admin and whoever they tick "shops.daymark" for; the accountant reads it. */}
            <Route path="shops/today" element={<P perm={['shops.daymark', 'shops.overview']}><s.ShopsToday /></P>} />
            <Route path="shop-home" element={<P perm="shop.home"><s.ShopHome /></P>} />
            <Route path="live-activity" element={<P perm="live.view"><s.LiveActivity /></P>} />
            <Route path="pos" element={<P perm="pos.sell"><s.Pos /></P>} />
            <Route path="bills" element={<P perm="sales.read"><s.Bills /></P>} />
            <Route path="bills/:id" element={<P perm="sales.read"><s.BillDetail /></P>} />
            {/* Edit bill: the counter that made it, the shop admin or the super admin (the server decides who). */}
            <Route path="bills/:id/edit" element={<P perm={['pos.sell', 'entries.delete']}><s.EditBill /></P>} />
            <Route path="products" element={<P perm="products.read"><s.Products /></P>} />
            {/* Raw materials of a shop that keeps an inventory (Shops & outlets → "Inventory of raw materials"). */}
            <Route path="inventory" element={<P perm={['inventory.read', 'inventory.operate']}><s.Inventory /></P>} />
            <Route path="shop-pnl" element={<P perm="shop.reports"><s.ShopPnl /></P>} />
            <Route path="shop-staff" element={<P perm="shop.users"><s.ShopStaff /></P>} />
            <Route path="shop-settings" element={<P perm="shop.settings"><s.ShopSettings /></P>} />
            {/* A shop's own maintenance requests (its admin; counters the admin ticked "maintenance.request" for). */}
            <Route path="shop-maintenance" element={<P perm="maintenance.request"><s.ShopMaintenance /></P>} />
            <Route path="cameras" element={<P perm="cameras.manage"><s.Cameras /></P>} />
            <Route path="areas" element={<P perm="settings.manage"><s.Areas /></P>} />
            <Route path="ev" element={<Navigate to="/admin/gate" replace />} />
            <Route path="parking" element={<Navigate to="/admin/gate" replace />} />
            <Route path="visitors" element={<P perm="visits.read"><s.Visitors /></P>} />
            <Route path="live" element={<P perm="reports.read"><s.LiveStatus /></P>} />
            {/* The parking cameras live, through the gate PC (the owner; security, the cashier and venue staff when granted). */}
            <Route path="cctv" element={<P perm="cctv.view"><s.CctvDashboard /></P>} />
            <Route path="games" element={<P perm="bookings.operate"><s.Games /></P>} />
            <Route path="turf" element={<P perm="bookings.operate"><s.Turf /></P>} />
            <Route path="events" element={<P perm={['bookings.operate', 'notifications.send']}><s.Events /></P>} />
            <Route path="karts" element={<P perm={['karts.operate', 'karts.manage']}><s.TrackDesk /></P>} />
            <Route path="karts/fleet" element={<P perm={['karts.operate', 'karts.manage', 'rides.manage']}><s.KartFleet /></P>} />
            <Route path="karts/report" element={<P perm={['karts.manage', 'rides.manage']}><s.KartReport /></P>} />
            {/* The appointment book of a spa, salon or any service outlet (venue logins pick the shop; shop logins see their own). */}
            <Route path="appointments" element={<P perm={['appointments.read', 'appointments.operate', 'appointments.manage']}><s.Appointments /></P>} />
            <Route path="settlements" element={<P perm="settlements.read"><s.Settlements /></P>} />
            <Route path="agreements" element={<P perm="settlements.manage"><s.Agreements /></P>} />
            <Route path="closures" element={<P perm="settlements.manage"><s.Closures /></P>} />
            <Route path="advertisements" element={<P perm="settlements.manage"><s.Advertisements /></P>} />
            <Route path="cleaning-charges" element={<P perm="settlements.manage"><s.CleaningCharges /></P>} />
            <Route path="generator" element={<P perm="settlements.manage"><s.Generator /></P>} />
            <Route path="electricity" element={<P perm="settlements.manage"><s.Electricity /></P>} />
            <Route path="dues" element={<P perm="settlements.manage"><s.Dues /></P>} />
            <Route path="ledger" element={<P perm="settlements.manage"><s.ShopLedger /></P>} />
            {/* A tenant shop's rent terms, balance and ledger (shop admins; the venue sees the same for a shop it opens). */}
            <Route path="rent" element={<P perm="settlements.read"><s.RentLedger /></P>} />
            <Route path="housekeeping" element={<P perm={[...FACILITY, 'housekeeping.operate']}><s.Housekeeping /></P>} />
            <Route path="quality" element={<P perm={FACILITY}><s.Quality /></P>} />
            <Route path="pest-control" element={<P perm={FACILITY}><s.PestControl /></P>} />
            {/* Head office (settlements.manage) reads the jobs for their costs and may charge a shop for one. */}
            <Route path="maintenance" element={<P perm={[...FACILITY, 'settlements.manage']}><s.Maintenance /></P>} />
            <Route path="assets" element={<P perm="facility.manage"><s.Assets /></P>} />
            <Route path="other-services" element={<P perm={FACILITY}><s.OtherServices /></P>} />
            <Route path="employees" element={<P perm={['people.operate', 'people.manage']}><s.Employees /></P>} />
            {/* Payroll (the accountant) reads the register and the Day report; the page keeps marking for HR and shop admins. */}
            <Route path="attendance" element={<P perm={['people.operate', 'people.manage', 'attendance.shop', 'payroll.manage']}><s.Attendance /></P>} />
            <Route path="payroll" element={<P perm="payroll.manage"><s.Payroll /></P>} />
            {/* Attendance operators may open it too: the page explains that reminders are for HR managers and payroll only. */}
            <Route path="hr-reminders" element={<P perm={['people.manage', 'payroll.manage', 'people.operate']}><s.HrReminders /></P>} />
            <Route path="accommodation" element={<P perm="people.operate"><s.Accommodation /></P>} />
            <Route path="materials" element={<P perm={SUPPLY}><s.Materials /></P>} />
            <Route path="material-requests" element={<P perm={SUPPLY}><s.MaterialRequests /></P>} />
            <Route path="procurement" element={<P perm="supply.manage"><s.Procurement /></P>} />
            <Route path="supplier-bills" element={<P perm="supply.manage"><s.SupplierBills /></P>} />
            <Route path="cards" element={<P perm="cards.operate"><s.Cards /></P>} />
            {/* The reception: load money into customers' wallets (the cashier, the super admin, venue staff with the grant). */}
            <Route path="reception" element={<P perm="wallet.recharge"><s.Reception /></P>} />
            {/* The reception cashier's start page: find or register a customer, load money, give the app setup code, today's summary. */}
            <Route path="reception-desk" element={<P perm="wallet.recharge"><s.ReceptionHome /></P>} />
            {/* Timed rides (go-karts, kids' toy cars) sold at the reception: ticket, countdown, overtime (the cashier; the kart desk too). */}
            <Route path="reception/rides" element={<P perm={['rides.sell', 'rides.manage', 'karts.operate', 'karts.manage']}><s.RidesDesk /></P>} />
            {/* Money loaded into wallets, spent from them by shop, the float held: head office. */}
            <Route path="reports/wallet" element={<P perm="wallet.manage"><s.WalletReport /></P>} />
            {/* Every point added, spent, returned or adjusted: head office and venue staff given it (the whole venue), a shop login its own shop ("Points received"). */}
            <Route path="points-history" element={<P perm="points.history"><s.PointsHistory /></P>} />
            <Route path="feedback" element={<P perm="feedback.read"><s.Feedback /></P>} />
            <Route path="terms" element={<P perm="terms.manage"><s.Terms /></P>} />
            {/* My business: the super admin's marketing and works; the Venue P&L also for the accountant. */}
            <Route path="venue/marketing" element={<P perm="venue.finance"><s.Marketing /></P>} />
            <Route path="venue/works" element={<P perm="venue.finance"><s.Works /></P>} />
            <Route path="earlier-records" element={<P perm="records.earlier"><s.EarlierRecords /></P>} />
            <Route path="venue/pnl" element={<P perm="venue.finance.read"><s.VenuePnl /></P>} />
            {/* Receipt printer for this device: shop counters (pos.sell) and the gate (visits.write). */}
            <Route path="printer" element={<P perm={['pos.sell', 'visits.write']}><s.PrinterSettings /></P>} />
            {/* My business: the owner's capital account, loans and liabilities; the accountant reads it. */}
            <Route path="venue/capital" element={<P perm="venue.finance.read"><s.Capital /></P>} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>

          <Route path="/app/login" element={<CustomerLogin />} />
          <Route
            path="/app/wallet/statement"
            element={
              <CustomerAuth>
                <c.WalletStatement />
              </CustomerAuth>
            }
          />
          <Route
            path="/app"
            element={
              <CustomerAuth>
                <CustomerLayout />
              </CustomerAuth>
            }
          >
            <Route index element={<c.Home />} />
            <Route path="wallet" element={<c.Wallet />} />
            <Route path="bills" element={<c.Bills />} />
            <Route path="pay" element={<c.Pay />} />
            <Route path="pay/:code" element={<c.Pay />} />
            {/* Points: the camera for the counter's bill QR; the bill itself (from its QR, or picked after scanning the shop's own QR). */}
            <Route path="scan" element={<c.ScanPay />} />
            <Route path="scan/:token" element={<c.ScanConfirm />} />
            <Route path="pay/:code/bill/:id" element={<c.ScanConfirm />} />
            <Route path="coupons" element={<c.Coupons />} />
            <Route path="offers" element={<c.Offers />} />
            <Route path="turf" element={<c.Turf />} />
            <Route path="history" element={<c.History />} />
            <Route path="inbox" element={<c.Inbox />} />
            <Route path="profile" element={<c.Profile />} />
            <Route path="mock-checkout/:paymentId" element={<c.MockCheckout />} />
            <Route path="feedback" element={<c.Feedback />} />
            <Route path="terms" element={<c.Terms />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
